/**
 * @file MetabolicControlDiabetesListView.jsx
 * @author Fernando Ruano
 * @description Metabolic Control Diabetes View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';

// Helpers
import {
  createColumns,
  getSearchApiKeys,
  getSorterString,
  getSelectFilter,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function MetabolicControlDiabetesListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, totalItems: totalItemsParams } = useParams();

  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getMetabolicControlDiabetesListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('dashboardStaff.lastHemoglobin'),
      dataIndex: 'ultima_hemoglobina',
      filter: {
        listName: 'lista_ultima_hba1c',
        searchName: 'filtroListaUltimaHba1c',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('ultima_hemoglobina'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_ultima_hba1c',
            searchName: 'filtroListaUltimaHba1c'
          },
          dataIndex: 'ultima_hemoglobina'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.lastHemoglobinDate'),
      dataIndex: 'fecha_ultima_hemoglobina',
      filter: {
        listName: 'lista_fecha_ultima_hba1c',
        searchName: 'filtroListaFechaUltimaHba1c',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_ultima_hemoglobina'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_fecha_ultima_hba1c',
            searchName: 'filtroListaFechaUltimaHba1c'
          },
          dataIndex: 'fecha_ultima_hemoglobina'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        idClinic: clinicId,
        controlSeleccionado: classification,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getMetabolicControlDiabetesListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            fecha_ultima_hemoglobina: item?.fecha_ultima_hemoglobina
              ? formatDate(item.fecha_ultima_hemoglobina)
              : ''
          };
        });

        setTotalItems(resp.data?.totalRegistros);
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };
  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      additionalColumns={additionalColumns}
      onPaginationChange={getetData}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
MetabolicControlDiabetesListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getMetabolicControlDiabetesListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default MetabolicControlDiabetesListView;
